const PDP_SELECTOR = 'main[data-template^="product."][data-template*="pdp-v2"]';
const CAROUSEL_SELECTOR = 'media-gallery.media-gallery--carousel';
const IMAGE_SELECTOR = 'slideshow-slide .product-media__image[alt]';
const MEDIA_WRAPPER_SELECTOR = '.product-media';
const FIT_GUIDE_SELECTOR = 'fieldset[data-afd-size-option="true"] .variant-option__legend-fit-guide';
const OVERLAY_CLASS = 'afd-pdp-carousel-overlay';
const OVERLAY_PAIR_CLASS = `${OVERLAY_CLASS}__pair`;
const OVERLAY_LABEL_CLASS = `${OVERLAY_CLASS}__label`;
const OVERLAY_VALUE_CLASS = `${OVERLAY_CLASS}__value`;
const OVERLAY_SEPARATOR_CLASS = `${OVERLAY_CLASS}__separator`;
const OVERLAY_FIT_CLASS = `${OVERLAY_CLASS}__fit`;
const OVERLAY_FIT_PREFIX_CLASS = `${OVERLAY_CLASS}__fit-prefix`;
const OVERLAY_FIT_VALUE_CLASS = `${OVERLAY_CLASS}__fit-value`;
const VARIANT_UPDATE_EVENT = 'variant:update';
const SIZE_TOKEN_PATTERN = /\[\s*(model_size|style_size|dress_size)\s*:\s*([^\]]*?)\s*\]/gi;

let isStarted = false;

/**
 * @param {string} altText
 * @returns {{ modelSize: string | null; dressSize: string | null }}
 */
export function parsePdpCarouselSizeTokens(altText = '') {
  const tokens = {
    modelSize: null,
    dressSize: null,
  };

  if (!altText) return tokens;

  SIZE_TOKEN_PATTERN.lastIndex = 0;
  for (const match of altText.matchAll(SIZE_TOKEN_PATTERN)) {
    const tokenName = String(match[1] || '').toLowerCase();
    const value = String(match[2] || '').replace(/\\n/g, '\n').trim();
    if (!value) continue;
    if (!/^[\w-]+$/i.test(value)) continue;

    if (tokenName === 'model_size' && !tokens.modelSize) {
      tokens.modelSize = value;
    }
    if ((tokenName === 'style_size' || tokenName === 'dress_size') && !tokens.dressSize) {
      tokens.dressSize = value;
    }
  }

  return tokens;
}

/**
 * @param {string} altText
 * @returns {string | null}
 */
export function parsePdpCarouselModelSizeLabel(altText = '') {
  return parsePdpCarouselSizeTokens(altText).modelSize;
}

/**
 * @param {string | null} modelSize
 * @param {string | null} dressSize
 * @returns {{ label: string; value: string }[]}
 */
function buildSizePairs(modelSize, dressSize) {
  if (modelSize && dressSize) {
    return modelSize === dressSize
      ? [{ label: 'Model & Style Size', value: modelSize }]
      : [
          { label: 'Model Size', value: modelSize },
          { label: 'Style Size', value: dressSize },
        ];
  }
  if (modelSize) return [{ label: 'Model & Style Size', value: modelSize }];
  if (dressSize) return [{ label: 'Model & Style Size', value: dressSize }];
  return [];
}

/**
 * @param {Element} pdpRoot
 * @returns {string | null}
 */
function getFitGuideValue(pdpRoot) {
  const fitGuideElement = pdpRoot.querySelector(FIT_GUIDE_SELECTOR);
  const fitGuideText = fitGuideElement?.textContent?.trim().replace(/^(?:Fits\s+)+/i, '') || '';
  return fitGuideText || null;
}

/**
 * @param {HTMLElement} mediaWrapper
 * @param {{ sizePairs: Array<{ label: string; value: string }>; fitLabel: string | null }} content
 */
function syncOverlayElement(mediaWrapper, content) {
  const overlay = mediaWrapper.querySelector(`:scope > .${OVERLAY_CLASS}`);
  const { sizePairs, fitLabel } = content;

  if (!sizePairs.length) {
    overlay?.remove();
    return;
  }

  const overlayElement = overlay || document.createElement('div');
  overlayElement.className = OVERLAY_CLASS;
  overlayElement.setAttribute('aria-hidden', 'true');
  overlayElement.replaceChildren();

  sizePairs.forEach((pair, index) => {
    const pairElement = document.createElement('span');
    pairElement.className = OVERLAY_PAIR_CLASS;

    const labelElement = document.createElement('span');
    labelElement.className = OVERLAY_LABEL_CLASS;
    labelElement.textContent = pair.label;
    pairElement.appendChild(labelElement);

    pairElement.appendChild(document.createTextNode(' '));

    const valueElement = document.createElement('span');
    valueElement.className = OVERLAY_VALUE_CLASS;
    valueElement.textContent = pair.value;
    pairElement.appendChild(valueElement);

    overlayElement.appendChild(pairElement);

    if (index < sizePairs.length - 1) {
      const separatorElement = document.createElement('span');
      separatorElement.className = OVERLAY_SEPARATOR_CLASS;
      separatorElement.textContent = '|';
      overlayElement.appendChild(separatorElement);
    }
  });

  if (fitLabel) {
    const fitElement = document.createElement('span');
    fitElement.className = OVERLAY_FIT_CLASS;
    const fitParts = fitLabel.match(/^(Fits)\s+(.*)$/i);
    if (fitParts) {
      const prefixElement = document.createElement('span');
      prefixElement.className = OVERLAY_FIT_PREFIX_CLASS;
      prefixElement.textContent = fitParts[1];
      fitElement.appendChild(prefixElement);
      fitElement.appendChild(document.createTextNode(' '));

      const valueElement = document.createElement('span');
      valueElement.className = OVERLAY_FIT_VALUE_CLASS;
      valueElement.textContent = fitParts[2];
      fitElement.appendChild(valueElement);
    } else {
      fitElement.textContent = fitLabel;
    }
    overlayElement.appendChild(fitElement);
  }

  if (!overlay) {
    mediaWrapper.appendChild(overlayElement);
  }
}

/**
 * @param {HTMLImageElement} image
 */
export function applyPdpCarouselOverlayToImage(image) {
  if (!(image instanceof HTMLImageElement)) return;
  if (!image.closest(PDP_SELECTOR) || !image.closest(CAROUSEL_SELECTOR)) return;

  const mediaWrapper = image.closest(MEDIA_WRAPPER_SELECTOR);
  if (!(mediaWrapper instanceof HTMLElement)) return;

  const pdpRoot = image.closest(PDP_SELECTOR);
  if (!(pdpRoot instanceof HTMLElement)) return;

  const { modelSize, dressSize } = parsePdpCarouselSizeTokens(image.getAttribute('alt') || '');
  const sizePairs = buildSizePairs(modelSize, dressSize);
  const fitGuideValue = getFitGuideValue(pdpRoot);

  syncOverlayElement(mediaWrapper, {
    sizePairs,
    fitLabel: fitGuideValue ? `Fits ${fitGuideValue}` : null,
  });
}

/**
 * @param {ParentNode} root
 */
export function refreshPdpCarouselOverlays(root = document) {
  const pdpRoot =
    root instanceof Document
      ? root.querySelector(PDP_SELECTOR)
      : root instanceof HTMLElement && root.matches(PDP_SELECTOR)
        ? root
        : root.querySelector(PDP_SELECTOR);

  if (!pdpRoot) return;

  const galleries = pdpRoot.querySelectorAll(CAROUSEL_SELECTOR);
  const images = galleries.length ? pdpRoot.querySelectorAll(IMAGE_SELECTOR) : [];

  images.forEach((image) => {
    applyPdpCarouselOverlayToImage(/** @type {HTMLImageElement} */ (image));
  });
}

export function startAfdPdpCarouselOverlays(root = document) {
  refreshPdpCarouselOverlays(root);

  if (isStarted || typeof document === 'undefined') return;

  document.addEventListener(VARIANT_UPDATE_EVENT, () => refreshPdpCarouselOverlays(document));
  isStarted = true;
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => startAfdPdpCarouselOverlays());
  } else {
    startAfdPdpCarouselOverlays();
  }
}
